const NARA_LAUNCH = Object.freeze({
  campaign: 'nara_launch_2026',
  targetIso: '2026-11-11T19:11:00.000Z',
  targetOffset: '2026-11-11T11:11:00-08:00',
  offerEndIso: '2026-11-12T07:11:00.000Z',
  offerEndOffset: '2026-11-11T23:11:00-08:00',
  autoOpenDelayMs: 3000,
  dismissDays: 7,
  cooldownMs: 12000,
});

const NARA_LAUNCH_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NARA_LAUNCH_FOCUS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function naraLaunchTargetMs(iso, fallbackIso) {
  const parsed = Date.parse(iso || fallbackIso);
  return Number.isFinite(parsed) ? parsed : Date.parse(fallbackIso);
}

function naraLaunchPhase(nowMs, startMs, offerEndMs) {
  if (nowMs < startMs) return 'before';
  if (nowMs < offerEndMs) return 'offer';
  return 'after';
}

function naraLaunchRemaining(nowMs, targetMs) {
  const delta = Math.max(0, targetMs - nowMs);
  return {
    total: delta,
    done: delta <= 0,
    days: Math.floor(delta / 86400000),
    hours: Math.floor((delta % 86400000) / 3600000),
    minutes: Math.floor((delta % 3600000) / 60000),
    seconds: Math.floor((delta % 60000) / 1000),
  };
}

function naraLaunchPad(value, size) {
  return String(Math.max(0, value)).padStart(size, '0');
}

function naraLaunchStorageKey(campaign, name) {
  return `nara.launch.${campaign}.${name}`;
}

function naraLaunchRead(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function naraLaunchWrite(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    /* private mode */
  }
}

function naraLaunchSessionRead(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function naraLaunchSessionWrite(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    /* private mode */
  }
}

function naraLaunchSessionClear(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    /* private mode */
  }
}

function naraLaunchIsEditor() {
  if (window.Shopify?.designMode || window.Shopify?.inspectMode) return true;
  return new URLSearchParams(location.search).has('preview_theme_id');
}

function naraLaunchOtherModalOpen(self) {
  const dialogs = [...document.querySelectorAll('dialog[open]')];
  if (dialogs.some((dialog) => dialog !== self)) return true;
  return Boolean(
    document.querySelector('[aria-modal="true"]:not([hidden]):not(.nara-launch)')
  );
}

function naraLaunchUserIsTyping() {
  const active = document.activeElement;
  if (!active) return false;
  const tag = active.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable;
}

class NaraLaunchPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.count = this.querySelector('[data-nara-launch-count]');
    this.countLabel = this.querySelector('[data-nara-launch-count-label]');
    this.offer = this.querySelector('[data-nara-launch-offer]');
    this.kicker = this.querySelector('[data-nara-launch-kicker]');
    this.signup = this.querySelector('[data-nara-launch-signup]');
    this.live = this.querySelector('[data-nara-launch-live]');
    this.status = this.querySelector('[data-nara-launch-status]');
    this.submitBtn = this.querySelector('[data-nara-launch-submit]');
    if (!this.dialog) return;

    this.campaign = this.dataset.campaign || NARA_LAUNCH.campaign;
    this.startMs = naraLaunchTargetMs(this.dataset.targetIso, NARA_LAUNCH.targetIso);
    this.offerEndMs = naraLaunchTargetMs(this.dataset.offerEndIso, NARA_LAUNCH.offerEndIso);
    this.autoOpenMs = Number(this.dataset.autoOpenMs) || NARA_LAUNCH.autoOpenDelayMs;
    this.dismissMs = (Number(this.dataset.dismissDays) || NARA_LAUNCH.dismissDays) * 86400000;
    this.previewLive = this.dataset.launchLive === 'true' && naraLaunchIsEditor();
    this.pendingKey = naraLaunchStorageKey(this.campaign, 'pending');
    this.dismissKey = naraLaunchStorageKey(this.campaign, 'dismissedAt');
    this.subscribedKey = naraLaunchStorageKey(this.campaign, 'subscribed');
    this.cooldownKey = naraLaunchStorageKey(this.campaign, 'cooldown');
    this.abort = new AbortController();
    this.lastFocus = null;
    this.timer = 0;
    this.openTimer = 0;
    this.closeTimer = 0;
    this.closing = false;
    this.phase = 'before';

    const { signal } = this.abort;
    this.querySelectorAll('[data-nara-launch-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close('dismiss');
      }, { signal });
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close('dismiss');
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close('dismiss');
    }, { signal });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event), { signal });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.tick();
    }, { signal });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-nara-launch-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open({ reason: 'trigger', restore: trigger });
    }, { signal });

    this.applyState();
    this.tick();
    this.startClock();
    this.restoreSubmission();
    this.scheduleAutoOpen();
  }

  disconnectedCallback() {
    this.clearTimers();
    this.abort?.abort();
  }

  currentPhase(nowMs = Date.now()) {
    if (this.previewLive) return 'offer';
    return naraLaunchPhase(nowMs, this.startMs, this.offerEndMs);
  }

  countdownTarget(phase = this.currentPhase()) {
    return phase === 'offer' ? this.offerEndMs : this.startMs;
  }

  remaining(nowMs = Date.now()) {
    const phase = this.currentPhase(nowMs);
    if (phase === 'after') {
      return { total: 0, done: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    return naraLaunchRemaining(nowMs, this.countdownTarget(phase));
  }

  isExpired(nowMs = Date.now()) {
    return this.currentPhase(nowMs) === 'after';
  }

  isSubscribed() {
    return naraLaunchRead(this.subscribedKey) === '1';
  }

  isDismissed() {
    const raw = naraLaunchRead(this.dismissKey);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < this.dismissMs;
  }

  applyState() {
    const phase = this.currentPhase();
    this.phase = phase;
    this.dataset.phase = phase;
    const showCount = phase === 'before' || phase === 'offer';
    const showOffer = phase === 'before' || phase === 'offer';
    const showLive = phase === 'offer' || phase === 'after';
    const showSignup = phase === 'before';

    if (this.kicker) {
      const coming = this.kicker.dataset.coming || 'NARA IS COMING';
      const live = this.kicker.dataset.live || 'NARA IS LIVE';
      this.kicker.textContent = showLive ? live : coming;
    }
    this.countLabel?.setAttribute('hidden', '');
    this.count?.toggleAttribute('hidden', !showCount);
    this.offer?.toggleAttribute('hidden', !showOffer);
    this.live?.toggleAttribute('hidden', !showLive);
    this.signup?.toggleAttribute('hidden', !showSignup);
    if (this.isSubscribed()) this.lockFormSuccess();
  }

  startClock() {
    this.clearClock();
    if (this.currentPhase() === 'after') return;
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  clearClock() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = 0;
    }
  }

  clearTimers() {
    this.clearClock();
    if (this.openTimer) {
      window.clearTimeout(this.openTimer);
      this.openTimer = 0;
    }
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = 0;
    }
  }

  tick() {
    const phase = this.currentPhase();
    if (phase !== this.phase) {
      this.applyState();
      if (phase === 'after') this.clearClock();
    }
    const left = this.remaining();
    if (!this.count) return;
    if (phase === 'after' || (left.done && phase !== 'offer')) {
      this.count.hidden = true;
      this.countLabel?.setAttribute('hidden', '');
      return;
    }
    this.count.hidden = false;
    this.writeUnit('days', naraLaunchPad(left.days, left.days > 99 ? 3 : 2));
    this.writeUnit('hours', naraLaunchPad(left.hours, 2));
    this.writeUnit('minutes', naraLaunchPad(left.minutes, 2));
  }

  writeUnit(name, value) {
    const node = this.querySelector(`[data-nara-launch-unit="${name}"]`);
    if (node) node.textContent = value;
  }

  canAutoOpen() {
    if (naraLaunchIsEditor()) return false;
    if (this.dialog.open) return false;
    if (this.isSubscribed()) return false;
    if (this.isDismissed()) return false;
    if (naraLaunchOtherModalOpen(this.dialog)) return false;
    if (naraLaunchUserIsTyping()) return false;
    return true;
  }

  scheduleAutoOpen() {
    if (naraLaunchSessionRead(this.pendingKey) === '1') return;
    if (!this.canAutoOpen()) return;
    const { signal } = this.abort;
    const tryOpen = () => {
      if (!this.canAutoOpen()) return;
      this.open({ reason: 'auto' });
    };
    const onScroll = () => {
      if (window.scrollY < 120) return;
      window.removeEventListener('scroll', onScroll);
      tryOpen();
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    if (window.scrollY >= 120) {
      this.openTimer = window.setTimeout(tryOpen, 400);
    }
  }

  restoreSubmission() {
    const pending = naraLaunchSessionRead(this.pendingKey) === '1';
    const posted = this.querySelector('[data-nara-launch-posted]')?.value === 'true';
    const hasErrors = this.querySelector('[data-nara-launch-has-errors]')?.value === 'true';
    if (!pending) {
      if (posted && this.isSubscribed()) this.lockFormSuccess();
      return;
    }
    naraLaunchSessionClear(this.pendingKey);
    if (posted) {
      naraLaunchWrite(this.subscribedKey, '1');
      this.lockFormSuccess();
      this.open({ reason: 'success' });
      return;
    }
    if (hasErrors) {
      this.open({ reason: 'error' });
      return;
    }
    this.setStatus('The launch list could not be updated. Try again.', 'error');
    this.open({ reason: 'error' });
  }

  open({ reason = 'trigger', restore = null } = {}) {
    if (this.closing) {
      this.dialog.classList.remove('is-leaving');
      this.closing = false;
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = 0;
      }
    }
    if (this.dialog.open) return;
    this.lastFocus = restore || document.activeElement;
    this.applyState();
    this.tick();
    this.dialog.classList.remove('is-leaving');
    this.dialog.showModal();
    this.dialog.focus({ preventScroll: true });
  }

  finishClose(intent = 'dismiss') {
    this.closing = false;
    this.dialog.classList.remove('is-leaving');
    if (this.dialog.open) this.dialog.close();
    if (intent === 'dismiss' && !this.isSubscribed()) {
      naraLaunchWrite(this.dismissKey, String(Date.now()));
    }
    const restore = this.lastFocus;
    this.lastFocus = null;
    if (restore && typeof restore.focus === 'function') {
      window.setTimeout(() => restore.focus({ preventScroll: true }), 0);
    }
  }

  close(intent = 'dismiss') {
    if (!this.dialog.open || this.closing) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      this.finishClose(intent);
      return;
    }
    this.closing = true;
    this.dialog.classList.add('is-leaving');
    const panel = this.querySelector('.nara-launch__panel');
    const done = (event) => {
      if (event) {
        if (event.target !== panel) return;
        const name = event.animationName || '';
        if (name && name.indexOf('nara-launch-out') === -1) return;
      }
      panel?.removeEventListener('animationend', done);
      panel?.removeEventListener('webkitAnimationEnd', done);
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = 0;
      }
      if (!this.closing) return;
      this.finishClose(intent);
    };
    panel?.addEventListener('animationend', done);
    panel?.addEventListener('webkitAnimationEnd', done);
    this.closeTimer = window.setTimeout(() => done(), 400);
  }

  onSubmit(event) {
    const honeypot = this.querySelector('.nara-launch__hp');
    if (honeypot?.value) {
      event.preventDefault();
      return;
    }
    const email = this.form?.elements.namedItem('contact[email]');
    const value = String(email?.value || '').trim();
    if (!NARA_LAUNCH_EMAIL.test(value)) {
      event.preventDefault();
      this.setStatus('Enter a valid email address.', 'error');
      email?.focus();
      return;
    }
    const last = Number(naraLaunchRead(this.cooldownKey) || 0);
    if (last && Date.now() - last < NARA_LAUNCH.cooldownMs) {
      event.preventDefault();
      this.setStatus('Please wait a moment before trying again.', 'error');
      return;
    }
    if (this.form?.dataset.submitting === 'true') {
      event.preventDefault();
      return;
    }
    naraLaunchWrite(this.cooldownKey, String(Date.now()));
    naraLaunchSessionWrite(this.pendingKey, '1');
    this.form.dataset.submitting = 'true';
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Sending…';
    }
  }

  lockFormSuccess() {
    const success =
      this.status?.querySelector('.nara-launch__message--success')?.textContent?.trim() ||
      'You’re on the list. We’ll remind you when Nara goes live.';
    this.setStatus(success, 'success');
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'You’re on the list';
    }
    const input = this.form?.elements.namedItem('contact[email]');
    if (input) input.disabled = true;
  }

  setStatus(message, tone) {
    if (!this.status) return;
    this.status.innerHTML = '';
    if (!message) return;
    const line = document.createElement('p');
    line.className = `nara-launch__message nara-launch__message--${tone}`;
    line.textContent = message;
    this.status.append(line);
  }
}

class NaraLaunchBanner extends HTMLElement {
  connectedCallback() {
    this.before = this.querySelector('[data-nara-banner-before]');
    this.live = this.querySelector('[data-nara-banner-live]');
    this.startMs = naraLaunchTargetMs(this.dataset.targetIso, NARA_LAUNCH.targetIso);
    this.offerEndMs = naraLaunchTargetMs(this.dataset.offerEndIso, NARA_LAUNCH.offerEndIso);
    this.abort = new AbortController();
    this.timer = 0;
    const { signal } = this.abort;
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.apply();
    }, { signal });
    window.addEventListener('pageshow', () => this.apply(), { signal });
    window.addEventListener('focus', () => this.apply(), { signal });
    window.addEventListener('resize', () => this.syncHeight(), { signal });
    this.apply();
    this.startClock();
  }

  disconnectedCallback() {
    this.clearClock();
    this.abort?.abort();
  }

  phase(nowMs = Date.now()) {
    return naraLaunchPhase(nowMs, this.startMs, this.offerEndMs);
  }

  startClock() {
    this.clearClock();
    if (this.phase() === 'after') return;
    this.timer = window.setInterval(() => this.apply(), 15000);
  }

  clearClock() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = 0;
    }
  }

  apply() {
    const phase = this.phase();
    if (phase === 'after') {
      this.hidden = true;
      this.syncHeight();
      this.clearClock();
      return;
    }
    this.hidden = false;
    this.before?.toggleAttribute('hidden', phase !== 'before');
    this.live?.toggleAttribute('hidden', phase !== 'offer');
    this.syncHeight();
  }

  syncHeight() {
    if (this.hidden) {
      document.documentElement.style.setProperty('--nara-launch-banner-h', '0px');
      return;
    }
    const action = this.querySelector('.nara-launch-banner__action:not([hidden])');
    const height = Math.round(action?.offsetHeight || this.scrollHeight || 0);
    document.documentElement.style.setProperty('--nara-launch-banner-h', `${height}px`);
  }
}

if (!customElements.get('nara-launch-popup')) {
  customElements.define('nara-launch-popup', NaraLaunchPopup);
}

if (!customElements.get('nara-launch-banner')) {
  customElements.define('nara-launch-banner', NaraLaunchBanner);
}

class NaraLaunchCloseNote extends HTMLElement {
  connectedCallback() {
    this.before = this.querySelector('[data-nara-close-before]');
    this.live = this.querySelector('[data-nara-close-live]');
    this.startMs = naraLaunchTargetMs(this.dataset.targetIso, NARA_LAUNCH.targetIso);
    this.offerEndMs = naraLaunchTargetMs(this.dataset.offerEndIso, NARA_LAUNCH.offerEndIso);
    this.abort = new AbortController();
    this.timer = 0;
    const { signal } = this.abort;
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.apply();
    }, { signal });
    window.addEventListener('pageshow', () => this.apply(), { signal });
    this.apply();
    this.startClock();
  }

  disconnectedCallback() {
    this.clearClock();
    this.abort?.abort();
  }

  phase(nowMs = Date.now()) {
    return naraLaunchPhase(nowMs, this.startMs, this.offerEndMs);
  }

  startClock() {
    this.clearClock();
    if (this.phase() === 'after') return;
    this.timer = window.setInterval(() => this.apply(), 15000);
  }

  clearClock() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = 0;
    }
  }

  apply() {
    const phase = this.phase();
    if (phase === 'after') {
      this.hidden = true;
      this.before?.toggleAttribute('hidden', true);
      this.live?.toggleAttribute('hidden', true);
      this.clearClock();
      return;
    }
    this.hidden = false;
    this.before?.toggleAttribute('hidden', phase !== 'before');
    this.live?.toggleAttribute('hidden', phase !== 'offer');
  }
}

if (!customElements.get('nara-launch-close-note')) {
  customElements.define('nara-launch-close-note', NaraLaunchCloseNote);
}

export { NARA_LAUNCH, naraLaunchPhase, naraLaunchRemaining, naraLaunchTargetMs };
